// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@synd/MobiusOverrides/Button/Button";
import React, { HTMLAttributes, ReactNode } from "react";

export interface CardWrapperProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
    icon?: React.ComponentType | string;
    iconSize?: number;
    title: string;
    secondaryText?: string;
    children: ReactNode;
    buttonUrl?: string;
    buttonText?: string;
    extendedStyles?: CardWrapperStyles;
}

export interface CardWrapperStyles {
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    title?: TypographyProps;
    secondaryText?: TypographyProps;
    button?: ButtonPresentationProps;
    typographyContainer?: InjectableCss;
    contentContainer?: InjectableCss;
}

export const cardWrapperStyles: CardWrapperStyles = {};

const TitledSection = ({
    title,
    icon,
    iconSize = 18,
    secondaryText,
    children,
    buttonText,
    buttonUrl,
    extendedStyles,
    ...htmlAttributes
}: CardWrapperProps) => {
    const [styles] = React.useState(() => mergeToNew(cardWrapperStyles, extendedStyles));
    const history = useHistory();
    const handleOnClick = (url: string) => {
        if (url?.startsWith("http")) {
            window.location.href = url;
        } else if (url) {
            history.push(url);
        }
    };
    return (
        <StyledWrapper {...styles.container} {...htmlAttributes}>
            <StyledWrapper {...styles.headerContainer}>
                <StyledWrapper {...styles.typographyContainer}>
                    {icon && <Icon src={icon} size={iconSize}></Icon>}
                    <Typography {...styles.title}>{title}</Typography>
                    {secondaryText && <Typography {...styles.secondaryText}>{secondaryText}</Typography>}
                </StyledWrapper>
                {buttonText && buttonUrl && (
                    <Button
                        variant={"link" as any}
                        onClick={() => handleOnClick(buttonUrl)}
                        {...styles.button}
                    >
                        {buttonText}
                    </Button>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.contentContainer}>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default TitledSection;
